export type InlineNode =
    | { kind: 'text'; text: string }
    | { kind: 'strong'; children: InlineNode[] }
    | { kind: 'em'; children: InlineNode[] }
    | { kind: 'link'; href: string; children: InlineNode[] };

export type BlockNode = { kind: 'paragraph'; children: InlineNode[] } | { kind: 'list'; items: InlineNode[][] };

const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

export function safeHref(raw: string): string | undefined {
    const href = raw.trim();

    const probe = href.replace(/[\u0000- \u007f-\u009f]/g, '');
    const match = /^([a-z][a-z0-9+.-]*:)/i.exec(probe);

    return match && SAFE_SCHEMES.has(match[1].toLowerCase()) ? href : undefined;
}

export function parseMarkdown(source: string): BlockNode[] {
    const blocks: BlockNode[] = [];

    for (const chunk of source.replace(/\r\n?/g, '\n').split(/\n[ \t]*\n/)) {
        let paragraph: string[] = [];
        let items: string[] | undefined;

        const flushParagraph = () => {
            if (paragraph.length) {
                blocks.push({ kind: 'paragraph', children: parseInline(paragraph.join(' ')) });
                paragraph = [];
            }
        };
        const flushList = () => {
            if (items) {
                blocks.push({ kind: 'list', items: items.map((item) => parseInline(item)) });
                items = undefined;
            }
        };

        for (const line of chunk.split('\n').filter((l) => l.trim() !== '')) {
            const bullet = /^\s*-\s+(.*)$/.exec(line);

            if (bullet) {
                flushParagraph();
                (items ??= []).push(bullet[1]);
            } else if (items) {
                items[items.length - 1] += ` ${line.trim()}`;
            } else {
                paragraph.push(line.trim());
            }
        }

        flushParagraph();
        flushList();
    }

    return blocks;
}

export function parseInline(source: string): InlineNode[] {
    const nodes: InlineNode[] = [];
    let text = '';
    let i = 0;

    const pushText = () => {
        if (text) {
            nodes.push({ kind: 'text', text });
            text = '';
        }
    };

    while (i < source.length) {
        if (source.startsWith('**', i)) {
            const close = source.indexOf('**', i + 2);

            if (close > i + 2) {
                pushText();
                nodes.push({ kind: 'strong', children: parseInline(source.slice(i + 2, close)) });
                i = close + 2;
                continue;
            }
        } else if (source[i] === '*') {
            const close = source.indexOf('*', i + 1);

            if (close > i + 1 && source[close + 1] !== '*') {
                pushText();
                nodes.push({ kind: 'em', children: parseInline(source.slice(i + 1, close)) });
                i = close + 1;
                continue;
            }
        } else if (source[i] === '[') {
            const link = /^\[([^\]]*)\]\(([^)\s]*)\)/.exec(source.slice(i));

            if (link) {
                const href = safeHref(link[2]);

                pushText();
                nodes.push(...(href ? [{ kind: 'link' as const, href, children: parseInline(link[1]) }] : parseInline(link[1])));
                i += link[0].length;
                continue;
            }
        }

        text += source[i];
        i++;
    }

    pushText();

    return nodes;
}
